// UserAccess — Account settings -> User access (RDV Systems admins): who can open which project.
// Every projectusers assignment as a folded tree - people, projects or roles, each opening onto
// its assignments - with each role editable in place, access removable, and a form to grant it. Backend: routes/access_routes.py
// (listing, role changes) and the per-project member endpoints (grant, remove).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import InvitationCodeNotice from '../UserManagement/InvitationCodeNotice';
import EmailIdentity from '../UserManagement/EmailIdentity';
import { apiError, changeRole, grantAccess, listAccess, removeAccess } from './userAccessApi';
import {
  describeGrant, filterAssignments, filterUnassigned, groupAssignments, matchProjects, onlyAdministrator, projectLabel,
} from './userAccessLogic';
import '../UserManagement/UserManagement.css';
import './UserAccess.css';

const GROUP_BY = [
  { id: 'person', label: 'Person' },
  { id: 'project', label: 'Project' },
  { id: 'role', label: 'Role' },
];
// The first column's heading: the group, then what it opens onto.
const FIRST_COLUMN = { person: 'Person / project', project: 'Project / person', role: 'Role / person' };
const keyOf = (row) => `${row.project_id}_${row.user_id}`;
const plural = (n, one, many) => `${n.toLocaleString()} ${n === 1 ? one : many}`;
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const NO_PROJECT_WARNING = 'No projects yet: this person can sign in but will see nothing.';

function NoProjectMark() {
  return <i className="fa-solid fa-triangle-exclamation ua-warn" title={NO_PROJECT_WARNING} aria-label={NO_PROJECT_WARNING} />;
}

function ProjectName({ row }) {
  return (
    <span className="ua-project">
      <Link to={`/projects/${row.project_id}`} className="ua-project__name">{projectLabel(row)}</Link>
      <span className="ua-project__id">#{row.project_id}</span>
    </span>
  );
}

export default function UserAccess() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [invitation, setInvitation] = useState(null);
  const [query, setQuery] = useState('');
  const [by, setBy] = useState('person');
  const [dir, setDir] = useState('asc');
  const [roleId, setRoleId] = useState(0);
  const [toggled, setToggled] = useState(() => new Set()); // groups flipped from the default
  const [busy, setBusy] = useState(null); // the row (or 'grant') being saved
  const [grant, setGrant] = useState(null); // the grant form's fields while it is open

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setData(await listAccess());
    } catch (e) {
      setError(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const assignments = useMemo(() => data?.assignments || [], [data]);
  const roles = data?.roles || [];
  const shown = useMemo(
    () => filterAssignments(assignments, { query, roleId }),
    [assignments, query, roleId],
  );
  // Accounts on no project (every Cognito account without an assignment) list too, so a person
  // can be given their first project from here.
  const unassigned = useMemo(() => data?.unassigned || [], [data]);
  const shownUnassigned = useMemo(
    () => filterUnassigned(unassigned, { query, roleId }),
    [unassigned, query, roleId],
  );
  const groups = useMemo(() => groupAssignments(shown, by, dir, shownUnassigned), [shown, by, dir, shownUnassigned]);
  // The tree starts folded; while searching every group with a match is open, so the hits
  // show. A click flips one group from that default; a new search or grouping starts over.
  const searching = query.trim() !== '';
  useEffect(() => { setToggled(new Set()); }, [query, by]);
  const isOpen = (key) => searching !== toggled.has(key);
  const toggle = (key) => setToggled((s) => {
    const next = new Set(s);
    if (next.has(key)) next.delete(key);
    else next.add(key);
    return next;
  });
  const allOpen = groups.length > 0 && groups.every((g) => isOpen(g.key));
  const setAll = (open) => setToggled(new Set(open !== searching ? groups.map((g) => g.key) : []));

  const people = useMemo(() => new Set(assignments.map((r) => (r.email || '').toLowerCase())).size, [assignments]);
  const projectCount = useMemo(() => new Set(assignments.map((r) => r.project_id)).size, [assignments]);

  const setRows = (fn) => setData((d) => ({ ...d, assignments: fn(d.assignments) }));
  const start = (key) => {
    setBusy(key);
    setError('');
    setNotice('');
  };

  const onRole = async (row, newRole) => {
    if (newRole === row.role_id) return;
    if (newRole !== 1 && onlyAdministrator(assignments, row)
        && !window.confirm(`${row.email} is the only Administrator of ${projectLabel(row)}. Change their role anyway?`)) {
      return;
    }
    start(keyOf(row));
    try {
      const res = await changeRole(row.project_id, row.user_id, newRole);
      setRows((rows) => rows.map((r) => (keyOf(r) === keyOf(row)
        ? { ...r, role_id: res.role_id, role_label: res.role_label } : r)));
      setNotice(`${row.email} is now ${res.role_label} on ${projectLabel(row)}.`);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  const onRemove = async (row) => {
    const onlyAdmin = onlyAdministrator(assignments, row) ? ' They are its only Administrator.' : '';
    if (!window.confirm(`Remove ${row.email}'s access to ${projectLabel(row)}?${onlyAdmin}`)) return;
    start(keyOf(row));
    try {
      await removeAccess(row.project_id, row.user_id);
      setRows((rows) => rows.filter((r) => keyOf(r) !== keyOf(row)));
      setNotice(`Removed ${row.email}'s access to ${projectLabel(row)}.`);
    } catch (e) {
      setError(apiError(e));
    } finally {
      setBusy(null);
    }
  };

  // The grant form: an email, a role and the projects ticked one by one in the picker
  // (`projectIds`, in the order ticked; `query` is the picker's search). Ticked projects stay
  // ticked across searches and show as chips. No select-all: each project is a deliberate tick.
  const openGrant = (prefill = {}) => {
    setError('');
    setGrant({ email: '', query: '', projectIds: [], role: 3, ...prefill });
  };
  const field = (name) => (e) => setGrant((g) => ({ ...g, [name]: e.target.value }));
  const tickProject = (id, on) => setGrant((g) => ({
    ...g, projectIds: on ? [...g.projectIds, id] : g.projectIds.filter((x) => x !== id),
  }));
  const [progress, setProgress] = useState(null); // {done, total} while granting several

  const onGrant = async (event) => {
    event.preventDefault();
    const email = grant.email.trim().toLowerCase();
    const projects = grant.projectIds.map((id) => data.projects.find((p) => p.id === id) || { id, name: null });
    if (!EMAIL.test(email)) {
      setError('Enter the person’s email address.');
      return;
    }
    if (!projects.length) {
      setError('Tick at least one project in the list.');
      return;
    }
    start('grant');
    setInvitation(null);
    const roleId = Number(grant.role);
    const outcome = { granted: [], already: [], failed: [] };
    let invitation = null;
    let roleLabel = roles.find((r) => r.id === roleId)?.label || '';
    let inviteFailed = false;
    for (let i = 0; i < projects.length; i += 1) {
      const project = projects[i];
      setProgress({ done: i + 1, total: projects.length });
      try {
        const res = await grantAccess(project.id, email, roleId); // eslint-disable-line no-await-in-loop
        const row = {
          project_id: project.id,
          project_name: project.name,
          user_id: res.user.user_id,
          email: res.user.email,
          role_id: res.user.role_id,
          role_label: res.user.role_label,
        };
        roleLabel = row.role_label || roleLabel;
        setData((d) => ({
          ...d,
          assignments: [...d.assignments, row],
          users: d.users.some((u) => u.id === row.user_id) ? d.users : [...d.users, { id: row.user_id, email: row.email }],
          unassigned: (d.unassigned || []).filter((u) => (u.email || '').toLowerCase() !== email),
        }));
        outcome.granted.push(project);
        invitation = invitation || res.invitation || null;
        inviteFailed = inviteFailed || res.invite_status === 'failed';
      } catch (e) {
        if (e?.response?.data?.code === 'USER_ALREADY_ASSIGNED') outcome.already.push(project);
        else outcome.failed.push([project, apiError(e)]);
      }
    }
    setProgress(null);
    setBusy(null);
    setInvitation(invitation);
    const text = describeGrant(email, roleLabel, outcome);
    if (!outcome.granted.length) {
      setError(text);
      return;
    }
    setNotice(text + (inviteFailed
      ? ' Their account still needs an invitation code: send one from User management.' : ''));
    // The form stays open with the same projects ticked, for the next person.
    setGrant((g) => ({ ...g, email: '' }));
  };
  const picked = grant ? grant.projectIds.map((id) => data?.projects.find((p) => p.id === id) || { id, name: null }) : [];
  const pick = grant && data ? matchProjects(grant.query, data.projects) : { matches: [], more: 0 };
  const unticked = grant ? pick.matches.filter((p) => !grant.projectIds.includes(p.id)) : [];

  const groupBy = (id) => {
    if (id === by) setDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else {
      setBy(id);
      setDir('asc');
    }
  };

  return (
    <div className="user-management user-management--embedded user-access">
      <header className="um-header">
        <p className="um-summary">
          {data
            ? `${plural(assignments.length, 'assignment', 'assignments')}: ${plural(people, 'person', 'people')} on ${plural(projectCount, 'project', 'projects')}`
            : loading ? 'Loading user access…' : ''}
          {unassigned.length > 0 && (
            <span className="ua-summary__warn">
              {`${plural(unassigned.length, 'person has', 'people have')} access to no project`}
            </span>
          )}
          {data?.unassigned_unavailable && (
            <span className="ua-summary__note">Accounts without a project could not be loaded.</span>
          )}
        </p>
        <div className="ua-head-actions">
          <button type="button" className="um-btn um-btn--quiet" onClick={load} disabled={loading}>
            {loading ? 'Refreshing…' : 'Refresh'}
          </button>
          <button type="button" className="um-btn um-btn--primary" onClick={() => openGrant()} disabled={!data}>
            Grant access
          </button>
        </div>
      </header>

      {error && <div className="um-error" role="alert">{error}</div>}
      {notice && <div className="ua-notice" role="status">{notice}</div>}
      <InvitationCodeNotice invitation={invitation} onClose={() => setInvitation(null)} />

      {grant && data && (
        <section className="um-panel ua-grant" aria-labelledby="ua-grant-title">
          <div className="um-panel__head">
            <h2 id="ua-grant-title" className="um-panel__title">Grant access</h2>
          </div>
          <p className="um-panel__hint">
            Someone new gets an account and an invitation code by email.
          </p>
          <form className="ua-grant__form" onSubmit={onGrant}>
            <label className="ua-field">
              <span>Email</span>
              <input type="email" className="um-field" list="ua-users" autoComplete="off" autoFocus={!grant.email}
                placeholder="name@company.com" value={grant.email} onChange={field('email')} />
            </label>
            <label className="ua-field">
              <span>Project</span>
              <input type="text" className="um-field" autoComplete="off" autoFocus={Boolean(grant.email)}
                placeholder="Search projects, then tick each one" value={grant.query} onChange={field('query')}
                onKeyDown={(e) => { if (e.key === 'Enter') e.preventDefault(); }} />
            </label>
            <label className="ua-field">
              <span>Role</span>
              <select className="ua-select" value={grant.role} onChange={field('role')}>
                {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
              </select>
            </label>
            <div className="ua-grant__actions">
              <button type="submit" className="um-btn um-btn--primary" disabled={busy === 'grant'}>
                {busy === 'grant'
                  ? (progress && progress.total > 1 ? `Granting ${progress.done} of ${progress.total}…` : 'Granting…')
                  : picked.length === 0 ? 'Grant access'
                    : `Grant access to ${plural(picked.length, 'project', 'projects')}`}
              </button>
              <button type="button" className="um-btn um-btn--quiet" onClick={() => setGrant(null)}>Cancel</button>
            </div>
            {/* The picker: the matches to tick on the left, the ticked ones on the right. Both panels
                keep their height and scroll inside, so a tick never moves the row above. */}
            <div className="ua-picker">
              <div className="ua-picklist" role="group" aria-label="Matching projects">
                <div className="ua-picklist__head">
                  <span>{`Matching projects (${pick.matches.length + pick.more})`}</span>
                  <span className="ua-picklist__head-right">
                    <span className={picked.length ? 'ua-picklist__ticked' : undefined}>
                      {picked.length ? `${picked.length} ticked` : 'Nothing ticked yet'}
                    </span>
                    {unticked.length > 0 && (
                      <button type="button" className="ua-picklist__clear" title={pick.more > 0 ? 'Only the projects shown; type more to narrow the list first' : undefined}
                        onClick={() => setGrant((g) => ({ ...g, projectIds: [...g.projectIds, ...unticked.map((p) => p.id)] }))}>
                        {`Tick all ${pick.matches.length} matching`}
                      </button>
                    )}
                  </span>
                </div>
                <div className="ua-picklist__body">
                  {pick.matches.length === 0 && <p className="ua-picklist__empty">No project matches.</p>}
                  {pick.matches.map((p) => (
                    <label key={p.id} className="ua-picklist__item">
                      <input type="checkbox" checked={grant.projectIds.includes(p.id)}
                        onChange={(e) => tickProject(p.id, e.target.checked)} />
                      <span>{p.name || 'Untitled'} <span className="ua-project__id">#{p.id}</span></span>
                    </label>
                  ))}
                  {pick.more > 0 && <p className="ua-picklist__more">{pick.more} more: type more to narrow the list.</p>}
                </div>
              </div>
              <div className="ua-picklist ua-picked" role="group" aria-label="Ticked projects">
                <div className="ua-picklist__head">
                  <span>{`Ticked (${picked.length})`}</span>
                  {picked.length > 0 && (
                    <button type="button" className="ua-picklist__clear" onClick={() => setGrant((g) => ({ ...g, projectIds: [] }))}>
                      Clear ticks
                    </button>
                  )}
                </div>
                <div className="ua-picklist__body">
                  {picked.length === 0 && <p className="ua-picklist__empty">Tick projects on the left; they stay ticked while you search.</p>}
                  {picked.length > 0 && (
                    <ul className="ua-chips">
                      {picked.map((p) => (
                        <li key={p.id} className="ua-chip">
                          {p.name || `Project #${p.id}`}
                          <button type="button" className="ua-chip__x" aria-label={`Remove ${p.name || `Project #${p.id}`}`}
                            onClick={() => tickProject(p.id, false)}>×</button>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              </div>
            </div>
          </form>
          <datalist id="ua-users">
            {data.users.map((u) => <option key={u.id} value={u.email} />)}
          </datalist>
        </section>
      )}

      <div className="ua-toolbar">
        <input
          type="search"
          className="um-search"
          aria-label="Search user access"
          placeholder="Search by email, company or project"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
        <div className="ua-segment" role="group" aria-label="Group by">
          <span className="ua-segment__label">Group by</span>
          {GROUP_BY.map((g) => (
            <button
              key={g.id}
              type="button"
              className={`ua-segment__btn${by === g.id ? ' is-on' : ''}`}
              aria-pressed={by === g.id}
              title={by === g.id ? 'Reverse the order' : undefined}
              onClick={() => groupBy(g.id)}
            >
              {g.label}
              {by === g.id && (
                <i className={`fa-solid ${dir === 'asc' ? 'fa-arrow-down' : 'fa-arrow-up'}`}
                  aria-label={dir === 'asc' ? 'ascending' : 'descending'} />
              )}
            </button>
          ))}
        </div>
        <select className="ua-select" aria-label="Role" value={roleId} onChange={(e) => setRoleId(Number(e.target.value))}>
          <option value={0}>All roles</option>
          {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
        </select>
        <button type="button" className="um-btn um-btn--quiet" onClick={() => setAll(!allOpen)} disabled={!groups.length}>
          {allOpen ? 'Collapse all' : 'Expand all'}
        </button>
      </div>

      <section className="um-panel" aria-label="User access">
        {groups.length === 0 ? (
          <p className="um-empty">
            {loading && !data ? 'Loading user access…'
              : query || roleId ? 'No access matches your search.' : 'No one has access to a project yet.'}
          </p>
        ) : (
          <div className="um-table-wrap">
            <table className="um-table ua-table">
              <thead>
                <tr>
                  <th scope="col">{FIRST_COLUMN[by]}</th>
                  {by === 'role' && <th scope="col">Project</th>}
                  <th scope="col">Role</th>
                  <th scope="col" className="um-table__actions-head"><span className="ua-sr">Actions</span></th>
                </tr>
              </thead>
              {groups.map((group) => {
                const open = isOpen(group.key);
                const n = group.rows.length;
                return (
                  <tbody key={group.key}>
                    <tr className={`ua-group${open ? ' is-open' : ''}`}>
                      <th colSpan={by === 'role' ? 4 : 3} scope="rowgroup">
                        <div className="ua-group__head">
                          <button type="button" className="ua-group__toggle" aria-expanded={open}
                            onClick={() => toggle(group.key)}>
                            <i className="fa-solid fa-chevron-right ua-group__chevron" aria-hidden="true" />
                            <span className="ua-group__label">
                              {by === 'person' && <EmailIdentity email={group.label || ''} />}
                              {by === 'person' && group.unassigned && <NoProjectMark />}
                              {by !== 'person' && group.unassigned && group.label}
                              {by === 'project' && !group.unassigned && (
                                <>
                                  {group.label}
                                  <span className="ua-project__id">#{group.sample.project_id}</span>
                                </>
                              )}
                              {by === 'role' && !group.unassigned && `${group.label}s`}
                            </span>
                            <span className="ua-group__count">
                              {by === 'person' && plural(n, 'project', 'projects')}
                              {by !== 'person' && group.unassigned && plural(group.people.length, 'person', 'people')}
                              {by === 'project' && !group.unassigned && plural(n, 'person', 'people')}
                              {by === 'role' && !group.unassigned && plural(n, 'assignment', 'assignments')}
                            </span>
                          </button>
                          {by === 'project' && !group.unassigned && (
                            <Link to={`/projects/${group.sample.project_id}`} className="ua-group__open"
                              aria-label={`Open ${group.label}`} title="Open the project">
                              <i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true" />
                            </Link>
                          )}
                          {(by === 'person' || (by === 'project' && !group.unassigned)) && (
                            <button
                              type="button"
                              className="um-btn um-btn--quiet ua-group__add"
                              onClick={() => openGrant(by === 'person'
                                ? { email: group.label }
                                : { projectIds: [group.sample.project_id] })}
                            >
                              {by === 'person' ? '+ Add project' : '+ Add person'}
                            </button>
                          )}
                        </div>
                      </th>
                    </tr>
                    {open && group.unassigned && by !== 'person' && group.people.map((p) => (
                      <tr key={p.email} className="ua-row ua-row--none">
                        <td className="ua-row__first"><EmailIdentity email={p.email || ''} /><NoProjectMark /></td>
                        {by === 'role' && <td className="ua-row__muted">—</td>}
                        <td className="ua-row__muted">—</td>
                        <td>
                          <div className="um-actions">
                            <button type="button" className="um-btn um-btn--quiet" onClick={() => openGrant({ email: p.email })}>
                              + Add project
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {open && group.rows.map((row) => {
                      const key = keyOf(row);
                      return (
                        <tr key={key} className="ua-row">
                          {by !== 'person' && <td className="ua-row__first"><EmailIdentity email={row.email || ''} /></td>}
                          {by !== 'project' && (
                            <td className={by === 'person' ? 'ua-row__first' : undefined}><ProjectName row={row} /></td>
                          )}
                          <td>
                            <select
                              className="ua-select ua-role"
                              aria-label={`Role of ${row.email} on ${projectLabel(row)}`}
                              value={row.role_id}
                              disabled={busy === key}
                              onChange={(e) => onRole(row, Number(e.target.value))}
                            >
                              {roles.map((r) => <option key={r.id} value={r.id}>{r.label}</option>)}
                            </select>
                          </td>
                          <td>
                            <div className="um-actions">
                              <button type="button" className="um-btn um-btn--danger" disabled={busy === key}
                                onClick={() => onRemove(row)}>
                                Remove
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                );
              })}
            </table>
          </div>
        )}
      </section>
    </div>
  );
}
